/**
 * WelcomeHelpModal Component
 *
 * Shown once after a user's first login to explain the contextual help system.
 * Introduces the "?" help button and how to use it.
 * Saves `helpWelcomeSeen: true` to the therapist's DB profile so it won't show again.
 */

import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import {
  Question,
  Lightbulb,
  CheckCircle,
  HandPointing,
  Headset,
} from "@phosphor-icons/react";
import {
  selectTherapistProfile,
  selectTherapistId,
} from "../../store/slices/authSlice";
import { useUpdateTherapistMutation } from "../../store/services/therapistApi";
import ModalCloseButton from "../ui/ModalCloseButton";
import "./HelpPopup.css"; // Reuse HelpPopup styles as base

function WelcomeHelpModal() {
  const [isVisible, setIsVisible] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const therapistProfile = useSelector(selectTherapistProfile);
  const therapistId = useSelector(selectTherapistId);
  const [updateTherapist] = useUpdateTherapistMutation();

  // Determine if we should show the modal
  useEffect(() => {
    if (!therapistProfile || !therapistId) return;

    // Don't show if already seen
    if (therapistProfile.helpWelcomeSeen === true) return;

    // Don't show if the user has localStorage flag
    const localKey = `clinikit_help_welcome_seen_${therapistId}`;
    if (localStorage.getItem(localKey) === "true") return;

    // Show with a small delay for smooth entrance
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 1500);

    return () => clearTimeout(timer);
  }, [therapistProfile, therapistId]);

  // Handle escape key
  useEffect(() => {
    if (!isVisible) return;
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        handleClose();
      }
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [isVisible]);

  const handleClose = useCallback(() => {
    setIsVisible(false);

    // Only persist "seen" if user checked "don't show again"
    if (dontShowAgain && therapistId) {
      localStorage.setItem(
        `clinikit_help_welcome_seen_${therapistId}`,
        "true"
      );
      updateTherapist({
        uid: therapistId,
        data: { helpWelcomeSeen: true },
      }).catch((err) =>
        console.error("[CK-HELP] Failed to save helpWelcomeSeen:", err)
      );
    }
  }, [therapistId, dontShowAgain, updateTherapist]);

  const handleOverlayClick = useCallback(
    (e) => {
      if (e.target === e.currentTarget) {
        handleClose();
      }
    },
    [handleClose]
  );

  if (!isVisible) return null;

  const modal = (
    <div className="help-popup-overlay" onClick={handleOverlayClick}>
      <div className="help-popup-modal welcome-help-modal">
        {/* Header */}
        <div className="help-popup-header">
          <div className="help-popup-icon welcome-help-icon">
            <Lightbulb size={28} weight="duotone" />
          </div>
          <h3 className="help-popup-title">
            היי{therapistProfile?.firstName ? ` ${therapistProfile.firstName}` : ""}! ברוך הבא למערכת!
          </h3>
          <ModalCloseButton onClick={handleClose} />
        </div>

        {/* Content */}
        <div className="help-popup-content">
          <p>
            שמת לב לכפתור <strong>סימן השאלה</strong>{" "}
            <span className="welcome-help-inline-icon">
              <Question size={14} weight="bold" />
            </span>{" "}
            שמופיע ליד הכותרות בעמודים שונים?
          </p>
          <p>
            <HandPointing size={16} weight="fill" style={{ verticalAlign: "middle", marginLeft: "4px" }} />
            לחיצה עליו תפתח <strong>הסבר קצר</strong> על הפיצ'ר או העמוד שאתה נמצא בו. אם משהו לא ברור, תוכל גם <strong>לשאול שאלה</strong> ישירות מתוך ההסבר וצוות התמיכה יחזור אליך.
          </p>
          <p>
            <Headset size={16} weight="fill" style={{ verticalAlign: "middle", marginLeft: "4px" }} />
            בנוסף, אנחנו תמיד זמינים דרך כפתור <strong>התמיכה</strong> בתפריט העליון -- לכל שאלה, בעיה, או עזרה בשימוש במערכת. אל תהססו לפנות!
          </p>

          {/* Visual example */}
          <div className="welcome-help-example">
            <div className="welcome-help-example-btn">
              <Question size={16} weight="bold" />
            </div>
            <span className="welcome-help-example-label">
              <CheckCircle size={14} weight="fill" />
              לחץ על סימן השאלה בכל מקום כדי לקבל עזרה
            </span>
          </div>
        </div>

        {/* Don't show again checkbox */}
        <label className="welcome-help-checkbox">
          <input
            type="checkbox"
            checked={dontShowAgain}
            onChange={(e) => setDontShowAgain(e.target.checked)}
          />
          <span>אל תציג שוב</span>
        </label>

        {/* Action */}
        <div className="help-popup-actions">
          <button
            className="welcome-help-dismiss-btn"
            onClick={handleClose}
            type="button"
          >
            הבנתי, תודה!
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}

export default WelcomeHelpModal;
